import { styled } from '@linaria/react';
import { ResizeHandle, TextDirectionProvider } from 'app-ui/primitives/layout';
import { MOBILE_VIEWPORT, themeCssVariables } from 'app-ui/theme';

const StyledResizeHandle = styled(ResizeHandle)`
  bottom: 0;
  justify-content: center;
  position: absolute;
  top: 0;
  z-index: 1;

  &[data-edge='left'] {
    left: 0;
  }

  &[data-edge='right'] {
    right: -${themeCssVariables.spacing[1]};
  }

  @media (max-width: ${MOBILE_VIEWPORT}px) {
    display: none;
  }
`;

const StyledLine = styled.div`
  background: transparent;
  height: 100%;
  transition: background calc(${themeCssVariables.animation.duration.fast} * 1s)
    ease;
  width: ${themeCssVariables.spacing[0.5]};

  [data-axis]:hover > &,
  [data-axis]:focus-visible > &,
  [data-axis]:active > & {
    background: ${themeCssVariables.border.color.strong};
  }
`;

type EdgeResizeHandleProps = {
  // The edge of the column the handle sits on: a column resized from its
  // left edge grows when the handle moves left.
  edge: 'left' | 'right';
  label: string;
  width: number;
  minimumWidth: number;
  maximumWidth: number;
  onWidthChange: (width: number) => void;
  onResizeStart: () => void;
  onResizeEnd: () => void;
};

// A thin line on the edge of a column, dragged with the pointer or moved with
// the arrow keys.
export const EdgeResizeHandle = ({
  edge,
  label,
  width,
  minimumWidth,
  maximumWidth,
  onWidthChange,
  onResizeStart,
  onResizeEnd,
}: EdgeResizeHandleProps) => (
  // In a right-to-left context the handle reverses its drag and arrow-key
  // directions, which is what a column growing to the left needs.
  <TextDirectionProvider direction={edge === 'left' ? 'rtl' : 'ltr'}>
    <StyledResizeHandle
      axis="x"
      data-edge={edge}
      aria-label={label}
      value={width}
      min={minimumWidth}
      max={maximumWidth}
      onValueChange={onWidthChange}
      onPointerDown={onResizeStart}
      onPointerUp={onResizeEnd}
      onPointerCancel={onResizeEnd}
    >
      <StyledLine />
    </StyledResizeHandle>
  </TextDirectionProvider>
);
